import styled from "@emotion/styled";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";

import { FONT_WEIGHT_SEMI_BOLD, Button as MuiButton } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { NautilusApiContextProvider } from "@skilitics/nautilus-core-designer/client";

import { useNautilusApi } from "../../api/useNautilusApi";
import { useFormatCreateModuleAccessDenied } from "../../errors/useFormatAccessDenied";
import { useAddModule, useFetchModuleList } from "../routes/modules/modulePersistence";
import { CreateModuleWizard, useCreateModuleSettings } from "../wizards/create-module";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 170px;

  font-size: var(--font-size-small);
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

const Button = styled(MuiButton)`
  display: flex;
  align-items: center;
  gap: 10px;

  width: 170px;
  height: 100%;

  font-size: var(--font-size-small);
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  border-radius: 10px;

  > i {
    padding-block-start: 4px;
  }
`;

export const AddButton = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const formatAccessDenied = useFormatCreateModuleAccessDenied();
  const { createAccess } = useFetchModuleList();

  const disabled = !createAccess?.granted;

  const api = useNautilusApi();

  const { metadata, reset } = useCreateModuleSettings();

  const [open, setOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const moduleId = useMemo(() => uuid(), []);

  const handleClick = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const { addModuleAsync, status } = useAddModule({ navigateOnCreate: true });

  const handleComplete = useCallback(async () => {
    try {
      await addModuleAsync({ id: moduleId, ...metadata });
      // don't close the dialog if there was an error, so the user can see it
      setOpen(false);
    } catch (error) {
      // fixme this is crap
      if (
        error instanceof Error &&
        error.name === "AxiosError" &&
        (error as any).response?.status === 403 &&
        (error as any).response?.data?.error === "AccessDeniedError"
      ) {
        setCreateError(formatAccessDenied((error as any).response.data.cause));
      } else {
        setCreateError(error instanceof Error ? error.message : String(error));
      }
    } finally {
      reset();
    }
  }, [addModuleAsync, metadata, moduleId, formatAccessDenied, reset]);

  return (
    <NautilusApiContextProvider api={api} hostId={moduleId}>
      <Container>
        <Button variant="contained" onClick={handleClick} disabled={disabled} data-test="add-module-button">
          {formatMessage("addNew")}
        </Button>
      </Container>
      <CreateModuleWizard
        busy={status === "pending"}
        open={open}
        errorMessage={createError}
        onCancel={handleClose}
        onClose={handleClose}
        onComplete={handleComplete}
        contentStyle={{ height: "700px" }}
        stepperStyle={{ padding: "0 45px 52px 45px" }}
      />
    </NautilusApiContextProvider>
  );
};

const formatStrings = {
  addNew: {
    en: "Add New",
  },
} satisfies FormatMessages;
